// 192 C.2 / B.5 — THE REACH READOUT, ONCE.
//
// MEASURED before this was written (2026-09-17, on `3bae4502`): the sentence
// that says how far back an e-mail search looked was written FOUR times —
// the section's count line (CaseEmailsSection.tsx:1342-1352), the section's
// zero-row sentence (`emptySentence`, :167-181), the drawer's results header
// (EmailDeepSearchDrawer.tsx:1065-1080) — and the drawer had NO zero-row
// sentence at all: its header renders only with rows, so a capped search that
// reached nothing said "No emails matched." and looked erratic (192 C.2).
// The words moved here verbatim first (one commit), then the drawer's zero-row
// mount (192 C.2) and the walk's own words (192 B.5, `cappedWithDate`).
//
// The shape follows `emailRowFilter.ts`: a lib hands the surfaces words as
// `t(key, { …vars, fallback })` calls through the `t` it is given, so the
// i18n gates see every literal key; nothing here is a module-level word map.
import { fmtDate } from "./emailArchiveClient";
import { fd } from "../utils";
import { periodBounds, localYMD, type PeriodKind } from "./periodRange";
import type { Translate } from "./interestRefusal";

/* 183 E.4a — the reach readout the server hangs off a search (m365.js,
   `searchUserMail`'s `.reach`), typed once here for both surfaces. */
export interface SearchReach {
  frameComplete: boolean;
  oldestReached: string | null;
  fetched: number;
  returned: number;
  /** 192 B.2 — true when the search WALKED its window ($filter), false on a
   *  `$search`; absent on a server older than 192, which is a `$search`.
   *  194: a walk runs as the completion (C.5) or the fallback (A.2d). */
  walked?: boolean;
  /** 194 A.2d — the fallback ran: Graph refused the composed clause. */
  searchRefused?: boolean;
  /** 194 C.5 — the completion's two numbers, and whether the fast search
   *  hit its ceiling inside the window. */
  found?: { search: number; walk: number; both: number; searchCeilingHit: boolean };
}
/** 194 C.5 — how the completion found a row; null when no completion ran. */
export type FoundBy = "search" | "walk" | "both";

/** Is this readout amber (the frame incomplete) or muted (complete / none)? */
export function reachCapped(reach: SearchReach | null | undefined): boolean {
  return !!reach && !reach.frameComplete;
}

/**
 * The readout beside a count of rows: "all in range shown — back to {date}"
 * when the frame is complete, the capped sentence when it is not. `null`
 * when there is no date to name (no reach, or nothing fetched in range).
 */
export function reachLine(reach: SearchReach | null | undefined, t: Translate): string | null {
  if (!reach?.oldestReached) return null;
  // `fmtDate` comes from an .mjs module and is `any` to the checker; the
  // String() is the seam that keeps this file at zero warnings (182's rule).
  const date = String(fmtDate(reach.oldestReached));
  if (reach.frameComplete) {
    return t("emailSearch.reachComplete", {
      date,
      fallback: `all in range shown — back to ${date}`,
    });
  }
  return cappedWithDate(reach, date, t);
}

/* 192 B.5 — THE READOUT MEANS TWO THINGS AND SAYS WHICH. A capped WALK
   (`reach.walked`) stopped inside its window because a valve fired — the
   window is wider than one request walks in its time — so the cure is a
   narrower DATE FRAME, which on a walk fetches less and reaches further. A
   capped `$search` hit Graph's 1,000-row ceiling over the terms' mail — 194
   A.2d: INSIDE the period, since the window now travels in the search string
   — and the cure is a narrower period, or the completion (C.5's checkbox,
   which walks the period beside the search). 192's `$search` keys said "give
   it a date frame (it then walks)", true then and false under 194, so a
   capped `$search` reads its own two NEW keys; the June/192 rows stay in the
   table, dead. The walk's are its own two keys, unchanged. */
function cappedWithDate(reach: SearchReach, date: string, t: Translate): string {
  if (reach.walked) {
    return t("emailSearch.reachCappedWalk", {
      date,
      fallback: `reached back only to ${date} — older messages exist in this range; narrow the date frame to see them`,
    });
  }
  return t("emailSearch.reachCappedSearch", {
    date,
    fallback: `reached back only to ${date} — older messages exist for these terms in this period, beyond the 1,000 one search returns; narrow the period, or tick “also search the whole period”`,
  });
}

/** 194 C.5 — the count line's second half when the completion ran: both
 *  numbers, and the overlap. `null` without a completion. */
export function foundLine(reach: SearchReach | null | undefined, t: Translate): string | null {
  const f = reach?.found;
  if (!f) return null;
  return t("emailSearch.found_counts", {
    search: f.search,
    walk: f.walk,
    both: f.both,
    fallback: `${String(f.search)} by the fast search · ${String(f.walk)} by the walk · ${String(f.both)} by both`,
  });
}

/** 194 C.5 — the mark a row wears when the completion ran: how it was found. */
export function foundByWord(foundBy: FoundBy | null | undefined, t: Translate): string | null {
  if (foundBy === "search") return t("emailSearch.found_by_search", { fallback: "fast search" });
  if (foundBy === "walk") return t("emailSearch.found_by_walk", { fallback: "walk" });
  if (foundBy === "both") return t("emailSearch.found_by_both", { fallback: "both" });
  return null;
}

/**
 * The sentence an EMPTY result shows (183 E.4a): the surface's own "none"
 * sentence only when the frame is complete; an incomplete frame is said with
 * the date the search stopped at, and a search that stopped before reaching
 * a single e-mail of the range (no date to name) says that instead.
 */
export function emptyReachSentence(
  reach: SearchReach | null | undefined,
  t: Translate,
  none: () => string,
): string {
  if (!reach || reach.frameComplete) return none();
  if (reach.oldestReached) return cappedWithDate(reach, String(fmtDate(reach.oldestReached)), t);
  if (reach.walked) {
    return t("emailSearch.reachNoneCappedWalk", {
      fallback: "The search stopped before reaching any e-mail in this range — narrow the date frame and search again.",
    });
  }
  return t("emailSearch.reachNoneCappedSearch", {
    fallback: "The search stopped before reaching any e-mail — narrow the period, or tick “also search the whole period”.",
  });
}

/* ── 194D D.2 [RULED — Laurent, 2026-09-18: a NEW calculation, replacing
   nothing] — HOW MUCH OF THE WINDOW THE FETCH DID NOT REACH. The reach
   sentences above say WHY a search stopped; the drawer's result row says how
   much is missing: the whole days between the window's start and the oldest
   e-mail actually served, when and only when the frame is incomplete
   (`SearchReach.frameComplete` false) and the window has a start. Nothing
   when the window was fully reached; nothing when the window has no start;
   nothing when the search reached no e-mail at all (no date to count from —
   the empty-result sentence says that). ONE definition, here, beside the two
   inputs it reads. Calendar days in UTC, the frame's own reading
   (`dateFrame.js` compares the ISO date prefix): the window's start is a
   `YYYY-MM-DD` and the oldest e-mail's date is its first ten characters, so
   2026-09-01 → an oldest of 2026-09-12 is 11 missing days. */
export function missingDays(reach: SearchReach | null | undefined, from: string): number | null {
  if (!reach || reach.frameComplete || !from || !reach.oldestReached) return null;
  const start = Date.parse(`${from}T00:00:00Z`);
  const oldest = Date.parse(`${reach.oldestReached.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(oldest)) return null;
  const days = Math.floor((oldest - start) / 86_400_000);
  return days > 0 ? days : null;
}

/* ── 197B §A.2 — THE NARROWING'S FRAME [RULED — Laurent, 2026-09-19] ──────────
   The e-mails drawer's « Limiter à » narrows the FETCHED rows to a period, in
   the browser, never searching again — and ONLY A PERIOD THE SEARCH ACTUALLY
   COVERED MAY BE CHOSEN: an option is pickable when its whole span lies inside
   the window the shown rows were fetched with (`meta.window`, 192 D.2's — what
   was ASKED, which is the frame even when the fetch stopped short of it: row 1
   already says, in amber, what was not reached); otherwise it reads « Hors
   recherche ». A narrowing the rows cannot honour is a label that lies. Three
   readings, ONE each, beside the two inputs they read:

   · THE OPTION'S SPAN is `periodBounds`' — LOCAL calendar dates through the one
     boundary function, with the SAME axis input the search rows' own windows
     take (`emailSearchWindow` passes null; `workweek`, the only kind that reads
     the axis, is in no list) — so « Ce mois » on the filter row is exactly the
     « Ce mois » the row above would search.
   · COVERAGE compares two `YYYY-MM-DD` pairs from that same producer (the
     window's edges are the rows' boxes — `periodBounds`' output, or a typed
     `<input type="date">` value) in code-point order; an EMPTY window edge is
     an open side (192 D.2) and covers everything there.
   · A ROW'S DATE against the span takes the SERVER'S frame reading, dateFrame.js
     `inRange`: the served `date` is Graph's `receivedDateTime`, a UTC instant,
     and its first ten characters (a UTC calendar day) are compared to the
     bounds as strings — the reading `missingDays` above already uses, and the
     code-point order `intervalSentence` sorts by. STATED, NOT SILENT: this is a
     CHOICE against the reader's LOCAL day. A mail received at 23:30Z on the
     12th is the 13th in Brussels; the server counted it inside a window ending
     on the 12th, so the narrowing counts it inside « the 12th » too — the row
     follows the frame it was fetched by, not the wall clock, and « Limiter à »
     over a period answers exactly the rows a search with that window answers. */

/** The span of a narrowing option: `[from, to]`, or null for "" (no narrowing).
 *
 *  197B-bis §A [RULED — Laurent, 2026-09-19]: THE SPAN IS CLIPPED AT TODAY.
 *  A calendar period running past today (« Cette semaine » on a Saturday
 *  ends tomorrow, « Ce mois » on the 19th ends on the 30th) is not a period
 *  the search failed to cover — NO E-MAIL EXISTS TOMORROW — so its end is
 *  the earlier of its own end and today's local date, HERE, in the one place
 *  both `spanCovered` and `dateInSpan` read, so the two cannot disagree.
 *  Clipped at TODAY and never at the WINDOW's end: a window that ends
 *  before today ends there because the reader asked for that, and mails
 *  after it may well exist unfetched — clipping there would call « Ce mois »
 *  covered by a search that stopped on the 19th while the 20th–30th were
 *  never fetched; nothing can exist after today, so a period truncated
 *  there is fully answered by the fetch whenever its start is inside the
 *  window. A period lying WHOLLY in the future would clip to an empty span
 *  (start after end) and is not offered (null) — none of the nine can:
 *  each is anchored on today or before it (measured). */
/*  198 §C.2 — AND THE SPAN READS THE FIRM'S START. `periodBounds`' axis input
 *  was `null` here because no kind in the list read the axis; the two
 *  accounting-year kinds do (198 §D.3 — the firm settings object carries the
 *  start through that same seam), so the drawer hands over what it holds and
 *  « Limiter à » narrows to the SAME exercise the search row above would
 *  search. A `null` still resolves — to 1 January — and is what a host with
 *  no settings in hand passes. */
export function narrowSpan(kind: PeriodKind, axisInput: unknown = null, now = new Date()): [string, string] | null {
  if (!kind || kind === "free") return null;
  const b = periodBounds({ kind }, axisInput, now);
  if (!b?.[0] || !b[1]) return null;
  const today = localYMD(now);
  const end = b[1] < today ? b[1] : today;
  return b[0] <= end ? [b[0], end] : null;
}

/** Is the span wholly inside the fetched window? An empty edge is unbounded. */
export function spanCovered(span: readonly [string, string], window: { from: string; to: string }): boolean {
  return (!window.from || span[0] >= window.from) && (!window.to || span[1] <= window.to);
}

/** Is the served date (a Graph ISO instant) inside the span — the server's own
 *  frame reading (its UTC day prefix against the bounds)? */
export function dateInSpan(date: string | null | undefined, span: readonly [string, string]): boolean {
  if (typeof date !== "string" || date.length < 10) return false;
  const d = date.slice(0, 10);
  return d >= span[0] && d <= span[1];
}

/** 194D D.2 — the amber line under the result row, or `null` when the window
 *  was reached (or has no start). */
export function missingDaysLine(reach: SearchReach | null | undefined, from: string, t: Translate): string | null {
  const n = missingDays(reach, from);
  if (n == null) return null;
  return t("emailSearch.days_missing", {
    count: n,
    fallback: `${String(n)} day${n === 1 ? "" : "s"} missing (narrow the period if necessary)`,
  });
}

/** 194D D.2 / E.4 — the interval of the rows actually served, from their
 *  dates; `null` with no rows. 206E addendum §A6 [ADVISOR — Laurent overrules
 *  at the glance]: OLDEST TO NEWEST — « dans l'intervalle du {oldest} au
 *  {youngest} » — as « du … au … » (from–to) reads and as the list below runs;
 *  194D D.2 had it newest first. The text changes, so the key is new
 *  (`emailSearch.result_interval_asc`; the seed only inserts). Both drawers
 *  read this one function. */
export function intervalSentence(dates: readonly (string | null | undefined)[], t: Translate): string | null {
  // ISO-8601 strings: the code-point order IS the time order, so the oldest
  // and the newest are the smallest and the largest string.
  let lo: string | null = null;
  let hi: string | null = null;
  for (const d of dates) {
    if (typeof d !== "string" || !d) continue;
    if (lo === null || d < lo) lo = d;
    if (hi === null || d > hi) hi = d;
  }
  if (lo === null || hi === null) return null;
  const oldest = fd(lo);
  const youngest = fd(hi);
  return t("emailSearch.result_interval_asc", {
    oldest,
    youngest,
    fallback: `in the interval from ${oldest} to ${youngest}`,
  });
}

/* ── 192 D.2 — THE WINDOW, SAID; A BOX THAT CANNOT BE READ, REFUSED ────────
   Laurent typed 31 November on prod: a native `<input type="date">` keeps
   showing the typed segments while its `value` is "" — and "" is an open side,
   so the search ran to today under a November heading. Two things follow, on
   both surfaces: the search REFUSES to run while a date box is in that state
   (`validity.badInput` is exactly that state, and was used nowhere in src/ —
   measured), naming WHICH box; and the count line states the window that was
   actually applied, so an open side can never look like a bound one. */

/** "01/11/2025 → today", "the beginning → 30/11/2025", "all dates" — the
 *  window a search applied, from the bounds it was SENT (not the boxes'
 *  current text). */
export function windowSentence(from: string, to: string, t: Translate): string {
  if (!from && !to) return t("emailSearch.window_all", { fallback: "all dates" });
  const a = from ? fd(from) : t("emailSearch.window_beginning", { fallback: "the beginning" });
  const b = to ? fd(to) : t("emailSearch.window_today", { fallback: "today" });
  return t("emailSearch.window_applied", { from: a, to: b, fallback: `${a} → ${b}` });
}

/** The refusal, naming the box, or `null` when both boxes are readable
 *  (a date, or empty). Read off the live elements at the moment of the click:
 *  React's onChange cannot see a box go from "" to unreadable, because its
 *  `value` is "" both before and after. */
export function dateBoxRefusal(
  fromBox: HTMLInputElement | null,
  toBox: HTMLInputElement | null,
  t: Translate,
): string | null {
  if (fromBox?.validity.badInput) {
    return t("emailSearch.date_box_unreadable_from", {
      fallback: "The From box does not hold a date it can read — correct it before searching.",
    });
  }
  if (toBox?.validity.badInput) {
    return t("emailSearch.date_box_unreadable_to", {
      fallback: "The To box does not hold a date it can read — correct it before searching.",
    });
  }
  return null;
}
